import { api, apiOrigin, TOKEN_KEY } from '../api';
import { apiErrorMessage } from '../utils/api-error';

export async function uploadFile(
  file: File,
  onProgress: (percentage: number) => void,
  signal: AbortSignal,
  folderId: string | null,
): Promise<void> {
  const session = await api.api.api.v1['upload-sessions'].post({
    name: file.name,
    size: file.size,
    mimeType: file.type || undefined,
    folderId,
  });
  if (session.error || !session.data) {
    throw new Error(
      apiErrorMessage(session.error, {
        fallback: `Could not start the upload for “${file.name}”.`,
        status: session.status,
      }),
    );
  }

  await new Promise<void>((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open(
      'PUT',
      `${apiOrigin}/api/v1/upload-sessions/${encodeURIComponent(session.data.id)}/content`,
    );
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) request.setRequestHeader('Authorization', `Bearer ${token}`);
    request.setRequestHeader('Content-Type', 'application/octet-stream');
    request.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100));
    });
    request.addEventListener('load', () => {
      if (request.status >= 200 && request.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      try {
        const error: unknown = JSON.parse(request.responseText);
        reject(
          new Error(
            apiErrorMessage(error, {
              fallback: `Upload failed for “${file.name}”.`,
              status: request.status,
            }),
          ),
        );
      } catch {
        reject(
          new Error(
            apiErrorMessage(undefined, {
              fallback: `Upload failed for “${file.name}”.`,
              status: request.status,
            }),
          ),
        );
      }
    });
    request.addEventListener('error', () =>
      reject(
        new Error(
          `Upload failed for “${file.name}”. The server could not be reached. Check your connection and try again.`,
        ),
      ),
    );
    request.addEventListener('abort', () =>
      reject(new DOMException('Upload cancelled', 'AbortError')),
    );
    signal.addEventListener('abort', () => request.abort(), { once: true });
    request.send(file);
  });
}
